/* eslint-disable complexity */
/* eslint-disable sonarjs/cognitive-complexity */
import config from 'Cloud/config';
import classNames from 'clsx';
import { ReactComponent as ComboIcon } from 'img/icons/vk_combo.svg';
import React, { memo, ReactElement, useEffect, useMemo, useRef, useState, VFC } from 'react';
import { Portal } from 'react-portal';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ACTION_PROMO } from 'reactApp/appHelpers/configHelpers';
import { isHideQuotaNewbieFeatureEnabled } from 'reactApp/appHelpers/featuresHelpers';
import { getFeatureAbSpace, getFeatureByName } from 'reactApp/modules/features/features.selectors';
import { PromoSelectors } from 'reactApp/modules/promo/promo.selectors';
import { EPromoType } from 'reactApp/modules/promo/promo.types';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { getComboSubscription, getProfessionalSubscription } from 'reactApp/modules/subscriptions/subscriptions.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { RootState } from 'reactApp/store';
import { Button } from 'reactApp/ui/Button/Button';
import { ComboPromo } from 'reactApp/ui/ComboPromo/ComboPromo';
import { ProTooltip } from 'reactApp/ui/ProTooltip/ProTooltip';
import { useSpaceCallbacks } from 'reactApp/ui/Space/hooks/useSpaceCallbacks';
import { SingleSpace } from 'reactApp/ui/Space/SingleSpace/SingleSpace';
import { Props } from 'reactApp/ui/Space/Space.types';
import { SpaceCountdown } from 'reactApp/ui/Space/SpaceCountdown/SpaceCountdown';
import { SpaceLoader } from 'reactApp/ui/Space/SpaceLoader/SpaceLoader';
import { SpaceProgress } from 'reactApp/ui/SpaceProgress/SpaceProgress';
import { myTeamParam } from 'reactApp/ui/TreeComponent/TreeComponent.data';
import { noop } from 'reactApp/utils/helpers';
import { openSubscriptionsPage } from 'reactApp/utils/openHelpers';

import styles from './Space.css';
import { getSpaceState, getSpaceText, getSubscriptionText } from './Space.helpers';

const IS_ACTION_USER = config.get('IS_ACTION_USER');
const IS_AUTO_TEST_PROMO = config.get('IS_AUTO_TEST_PROMO');

const Label: VFC<{
    proSubscription?: boolean;
    comboSubscription?: Record<string, unknown>;
    handleProClick: () => void;
    isMobile?: boolean;
}> = ({ proSubscription, comboSubscription, handleProClick, isMobile }) => {
    if (proSubscription) {
        return (
            <div className={styles.pro} onClick={isMobile ? undefined : handleProClick}>
                PRO
            </div>
        );
    }
    if (comboSubscription) {
        return <ComboIcon height={20} width={77} className={styles.combo} />;
    }

    return null;
};

const SpaceComponent = memo<Props>(
    // eslint-disable-next-line max-lines-per-function
    ({ isSubscriptionsPage, goToSubscriptions = openSubscriptionsPage, isMobile, onClose = noop }) => {
        const subscriptionsRef = useRef<HTMLDivElement>(null);
        const [showSpacePro, setShowSpacePro] = useState(false);

        const userEmail = useSelector(UserSelectors.getEmail);
        const storage = useSelector(getCurrentStorage);
        const comboSubscription = useSelector(getComboSubscription);
        const comboPromo: any = useSelector((state: RootState) => getFeatureByName(state, 'combo-promo'));
        const proSubscription = useSelector(getProfessionalSubscription);
        const { isLoaded: isSingleQuotaLoaded } = useSelector(UserQuotaSelectors.getLoadingState);
        const isPaidUser = useSelector(UserSelectors.isPaidUser);
        const isB2BUser = useSelector(UserSelectors.isB2BUser);
        const isOldBizUser = useSelector(UserSelectors.isOldBizUser);
        const isBizUser = useSelector(UserSelectors.isBizUser);
        const countdownPromo = useSelector(PromoSelectors.getPromo(EPromoType.space));
        const { isOverQuota, used, remaining, userTotalSpace } = useSelector(UserQuotaSelectors.getUserQuotaState);

        const isAction = IS_ACTION_USER && !isOverQuota && (!!ACTION_PROMO || IS_AUTO_TEST_PROMO);
        const spaceText = getSpaceText({ isMobile, used, userTotalSpace, isOverQuota, remaining });
        const { isReactPage } = getStorage(storage);
        const subscriptionText = getSubscriptionText(isPaidUser, isMobile);
        const modificator = isAction ? ACTION_PROMO : '';
        const spaceState = getSpaceState(modificator, isOverQuota);
        const isNewbie = useSelector(UserSelectors.isNewbie);

        const { onPayment, onShow, onSubscriptionClick } = useSpaceCallbacks({
            isBizUser,
            isSubscriptionsPage,
            countdownPromo,
            isAction,
            isB2BUser,
            isOldBizUser,
            isPaidUser,
            goToSubscriptions,
            isMobile,
        });

        const handleProClick = () => {
            setShowSpacePro(!showSpacePro);
        };

        useEffect(() => {
            onShow();
        }, [onShow]);

        const buttonText = useMemo(() => {
            return isAction ? 'Новый год в Облаке' : 'Увеличить объем';
        }, [isAction]);

        const button = useMemo(
            () => (
                <Button primary fluid theme={isMobile ? 'vk' : 'octavius'} onClick={onPayment}>
                    <div className={styles.buttonText}>{buttonText}</div>
                </Button>
            ),
            [buttonText, isMobile, onPayment]
        );

        // tempexp_14483-next-line
        // CLOUDWEB-14483: Показываем скелетом до тех пор пока юзер не принял лс и пока не отработало преименнование файлов (под фичей).
        if (!isSingleQuotaLoaded || (isHideQuotaNewbieFeatureEnabled && isNewbie)) {
            return (
                <div
                    className={classNames(styles.root, {
                        [styles.root_subscriptions]: isSubscriptionsPage,
                        [styles.root_noButton]: isB2BUser,
                    })}
                >
                    <SpaceLoader withButton={!isSubscriptionsPage && !isB2BUser} />
                </div>
            );
        }

        const subscriptionLink = isReactPage ? (
            <Link
                to={{
                    pathname: `/subscriptions${myTeamParam}`,
                }}
            >
                <div className={styles.subscriptions} ref={subscriptionsRef} onClick={onClose}>
                    {subscriptionText}
                </div>
            </Link>
        ) : (
            <div className={styles.subscriptions} ref={subscriptionsRef} onClick={onSubscriptionClick}>
                {subscriptionText}
            </div>
        );

        return (
            <div
                className={classNames(styles.root, {
                    [styles.root_overquota]: isOverQuota,
                    [styles.root_mobile]: isMobile,
                    [styles[`root_${modificator}`]]: !!modificator,
                    [styles.root_subscriptions]: isSubscriptionsPage,
                    [styles.root_noButton]: isB2BUser,
                    [styles.change_padding]: isReactPage,
                    [styles.root_marketingPromo]: isAction,
                })}
                data-qa-id={`space:${spaceState}`}
            >
                <div className={styles.infoBlock}>
                    <div className={styles.header}>
                        <div className={styles.tariff}>{isMobile ? userEmail : userTotalSpace?.value}</div>
                        <Label
                            isMobile={isMobile}
                            proSubscription={proSubscription}
                            comboSubscription={comboSubscription}
                            handleProClick={handleProClick}
                        />
                    </div>
                    <SpaceProgress />
                    <div className={styles.space}>
                        <div className={styles.spaceText}>{spaceText}</div>
                        {!isBizUser && !isSubscriptionsPage && subscriptionLink}
                    </div>
                    {showSpacePro && (
                        <Portal>
                            <ProTooltip target={subscriptionsRef} onClose={handleProClick} santa={proSubscription.santa} />
                        </Portal>
                    )}
                </div>
                {countdownPromo ? (
                    <div className={styles.promo}>
                        <SpaceCountdown onClick={onPayment} button={button} />
                    </div>
                ) : (
                    !isB2BUser &&
                    !isSubscriptionsPage && (
                        <div className={styles.payment}>
                            {button}
                            {comboPromo && !isPaidUser && !isBizUser && (
                                <ComboPromo href={comboPromo.spaceUnder} description={`Получить +${comboPromo.space} c`} under />
                            )}
                        </div>
                    )
                )}
            </div>
        );
    }
);

SpaceComponent.displayName = 'SpaceComponent';

// tempexp_14278-start
export const Space = memo((props: Props): ReactElement => {
    const abSpace = useSelector(getFeatureAbSpace);

    return abSpace === 'b' ? <SingleSpace {...props} /> : <SpaceComponent {...props} />;
});
// tempexp_14278-end

Space.displayName = 'Space';
